import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { 
  ArrowLeft,
  Reply,
  ReplyAll,
  Forward,
  Mail,
  Calendar,
  User,
  Paperclip,
  AlertTriangle,
  CheckCircle,
  Send,
  X,
  Plus,
  AtSign,
  Type,
  Download,
  Contrast,
  UserMinus,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { PageSkeleton } from './Skeleton';
import VariableInsertPanel, {
  insertTokenIntoInput,
  insertTokenIntoQuill,
} from './VariableInsertPanel';
import { useUnreadInbox } from '../context/UnreadInboxContext';
import { useMailPreferences } from '../context/MailPreferencesContext';
import { useEmailHtmlDisplay } from '../hooks/useEmailHtmlDisplay';
import { buildOriginalQuoteHtml, combineReplyAndQuote, isEmptyRichText, readCachedMailPreferences } from '../utils/mailPreferences';
import { parseMailDate } from '../utils/mailDates';
import EmailHtmlBody from './EmailHtmlBody';
import { quillHtmlToEmailHtml } from '../utils/quillEmailHtml';
import {
  emailComposerFormats,
  emailComposerKeyboard,
  emailComposerToolbar,
  prepareEmailComposerQuill,
} from '../utils/quillToolbar';
import RecipientSuggestInput from './RecipientSuggestInput';
import EmailComposerAiButtons from './EmailComposerAiButtons';
import {
  applyHtmlToComposer,
  buildIncomingEmailPayload,
  plainTextToQuillHtml,
  requestEmailAssist,
} from '../utils/emailAiAssist';

const withSeenFlag = (flags = []) => {
  const next = Array.isArray(flags) ? [...flags] : [];
  if (!next.includes('\\Seen')) next.push('\\Seen');
  return next;
};

const EmailDetail = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { refreshUnreadCount, decrementUnread } = useUnreadInbox();
  const { preferences } = useMailPreferences();
  const { isDarkMode, invertEmailInDark, toggleEmailInvert, emailHtmlClassName } = useEmailHtmlDisplay();
  const allowImagesByDefault = !preferences.blockExternalContent;
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState('Verbindung wird hergestellt...');
  const [showComposer, setShowComposer] = useState(false);
  const [composerMode, setComposerMode] = useState('reply'); // reply, replyAll, forward, new
  const [composerData, setComposerData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    text: '',
    html: '',
    inReplyTo: '',
    references: ''
  });
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [allowExternalImages, setAllowExternalImages] = useState(
    () => !readCachedMailPreferences().blockExternalContent
  );
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [aiReplyLoading, setAiReplyLoading] = useState(false);
  const [composerQuoteHtml, setComposerQuoteHtml] = useState('');
  const [composerEditorKey, setComposerEditorKey] = useState(0);
  const [templateVars, setTemplateVars] = useState([]);
  const [insertTarget, setInsertTarget] = useState('content');
  const quillRef = useRef(null);
  const markedReadRef = useRef(false);
  const composerHtmlRef = useRef('<p><br></p>');
  const subjectRef = useRef(null);

  useEffect(() => {
    markedReadRef.current = false;
    setAllowExternalImages(allowImagesByDefault);
    if (uid) {
      loadEmailDetails();
    }
  }, [uid]);

  useEffect(() => {
    setTemplateVars([
      { key: 'contact_name', label: 'Name des Kontakts', source: 'contact:name', value: '', options: {} },
      { key: 'contact_company', label: 'Unternehmen', source: 'contact:company', value: '', options: {} },
      { key: 'contact_email', label: 'E-Mail', source: 'contact:email', value: '', options: {} },
      { key: 'contact_phone', label: 'Telefon', source: 'contact:phone', value: '', options: {} },
      { key: 'contact_website', label: 'Website', source: 'contact:website', value: '', options: {} },
      { key: 'contact_anrede', label: 'Anrede (Sehr geehrte…)', source: 'contact:anrede', value: '', options: {} },
    ]);
  }, []);
  const markAsRead = async (loadedEmail) => {
    if (!uid || markedReadRef.current) return;
    if (loadedEmail?.flags?.includes('\\Seen')) {
      markedReadRef.current = true;
      return;
    }
    markedReadRef.current = true;
    setEmail((prev) => (prev ? { ...prev, flags: withSeenFlag(prev.flags) } : prev));
    decrementUnread(1);
    try {
      await axios.post(`/api/emails/${uid}/read`, null, { timeout: 15000 });
      refreshUnreadCount();
    } catch (error) {
      console.error('Konnte E-Mail nicht als gelesen markieren:', error);
      refreshUnreadCount();
    }
  };

  const loadEmailDetails = async () => {
    setLoading(true);
    setLoadingProgress('E-Mail wird geladen...');
    try {
      const response = await axios.get(`/api/emails/${uid}`, { timeout: 25000 });
      if (response.data.success && response.data.email) {
        setEmail(response.data.email);
        markAsRead(response.data.email);
      } else {
        toast.error(response.data?.error || 'E-Mail nicht gefunden');
        setEmail(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Laden der E-Mail');
      setEmail(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = parseMailDate(dateString);
    if (!date) return dateString || '';
    return date.toLocaleString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const extractEmail = (str) => str ? (str.match(/<(.+)>/) ? str.match(/<(.+)>/)[1] : str) : '';
  const extractName = (str) => {
    if (!str) return '';
    const match = str.match(/^(.+)\s*<.+>$/);
    return match ? match[1].replace(/"/g, '').trim() : extractEmail(str);
  };

  const unsubscribeFromEmail = async () => {
    if (!email?.uid || !email?.subscription?.canUnsubscribe) return;
    const from = extractName(email.from) || extractEmail(email.from) || email.from || 'diesem Absender';
    if (!window.confirm(`Von „${from}“ abmelden?`)) return;
    try {
      setUnsubscribing(true);
      const res = await axios.post(`/api/emails/${email.uid}/unsubscribe`, {
        mailbox: email.mailbox || 'INBOX',
      });
      if (res.data?.openUrl) {
        window.open(res.data.openUrl, '_blank', 'noopener,noreferrer');
        toast.success(
          res.data?.result?.needsBrowser
            ? 'Abmelde-Seite geöffnet — bitte dort bestätigen'
            : 'Abmeldung ausgelöst'
        );
      } else if (res.data?.result?.method === 'mailto') {
        toast.success('Abmelde-Mail wurde gesendet');
      } else {
        toast.success('Abmeldung ausgelöst');
      }
      setEmail((prev) =>
        prev
          ? {
              ...prev,
              subscription: prev.subscription
                ? { ...prev.subscription, canUnsubscribe: false }
                : prev.subscription,
            }
          : prev
      );
    } catch (err) {
      const openUrl = err.response?.data?.openUrl || email.subscription?.httpUrl;
      if (openUrl) {
        window.open(openUrl, '_blank', 'noopener,noreferrer');
        toast.error('Automatische Abmeldung fehlgeschlagen — Link geöffnet');
      } else {
        toast.error(err.response?.data?.error || 'Abmelden fehlgeschlagen');
      }
    } finally {
      setUnsubscribing(false);
    }
  };

  const openComposer = (mode) => {
    setComposerMode(mode);
    const shouldQuote = mode === 'new'
      ? false
      : (mode === 'forward' ? true : preferences.replyQuoteOriginal);
    const quoteHtml = shouldQuote
      ? buildOriginalQuoteHtml(email, {
          formatDate,
          mode: mode === 'forward' ? 'forward' : 'reply',
        })
      : '';
    setComposerQuoteHtml(quoteHtml);
    
    switch (mode) {
      case 'reply':
        setComposerData({
          to: extractEmail(email.from),
          cc: '',
          bcc: '',
          subject: email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`,
          text: '',
          html: '<p><br></p>',
          inReplyTo: email.messageId,
          references: email.messageId
        });
        break;
        
      case 'replyAll':
        setComposerData({
          to: extractEmail(email.from),
          cc: email.cc || '',
          bcc: '',
          subject: email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`,
          text: '',
          html: '<p><br></p>',
          inReplyTo: email.messageId,
          references: email.messageId
        });
        break;
        
      case 'forward':
        setComposerData({
          to: '',
          cc: '',
          bcc: '',
          subject: email.subject.startsWith('Fwd: ') ? email.subject : `Fwd: ${email.subject}`,
          text: '',
          html: '<p><br></p>',
          inReplyTo: '',
          references: ''
        });
        break;
        
      case 'new':
        setComposerData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          text: '',
          html: '<p><br></p>',
          inReplyTo: '',
          references: ''
        });
        break;
    }
    
    composerHtmlRef.current = '<p><br></p>';
    setComposerEditorKey((k) => k + 1);
    setShowComposer(true);
    setShowCc(false);
    setShowBcc(false);
    setShowEmoji(false);
  };

  const handleComposerChange = (field, value) => {
    setComposerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const insertComposerPlaceholder = (key) => {
    const token = `{{${key}}}`;
    if (insertTarget === 'subject') {
      insertTokenIntoInput(
        subjectRef.current,
        composerData.subject,
        token,
        (next) => handleComposerChange('subject', next)
      );
      return;
    }
    if (!insertTokenIntoQuill(quillRef, token)) {
      const html = composerHtmlRef.current || composerData.html || '<p><br></p>';
      const next = html.replace(/<\/p>\s*$/i, `${token}</p>`);
      composerHtmlRef.current = next.includes(token) ? next : `${html}${token}`;
      setComposerEditorKey((k) => k + 1);
    }
  };

  const sendEmail = async () => {
    let messageHtml = composerHtmlRef.current || composerData.html;
    try {
      const editor = quillRef.current?.getEditor?.();
      if (editor) messageHtml = editor.root.innerHTML;
    } catch (_) {}

    if (!composerData.to.trim() || !composerData.subject.trim()) {
      toast.error('Bitte Empfänger und Betreff ausfüllen');
      return;
    }
    if (isEmptyRichText(messageHtml) && !composerData.text.trim()) {
      toast.error('Bitte eine Nachricht schreiben');
      return;
    }
    
    setSending(true);
    
    try {
      const finalHtml = combineReplyAndQuote(quillHtmlToEmailHtml(messageHtml), composerQuoteHtml);
      
      const response = await axios.post('/api/send-email-reply', {
        ...composerData,
        html: finalHtml,
      });
      
      if (response.data.success) {
        toast.success('E-Mail erfolgreich gesendet!');
        setShowComposer(false);
        setComposerQuoteHtml('');
        setComposerData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          text: '',
          html: '',
          inReplyTo: '',
          references: ''
        });
      } else {
        toast.error('Fehler beim Senden der E-Mail');
      }
    } catch (error) {
      console.error('Fehler beim Senden der E-Mail:', error);
      toast.error('Fehler beim Senden: ' + (error.response?.data?.error || error.message));
    } finally {
      setSending(false);
    }
  };

  const quillModules = useMemo(() => ({
    toolbar: emailComposerToolbar,
    clipboard: { matchVisual: false },
    keyboard: emailComposerKeyboard,
  }), []);

  const quillFormats = useMemo(() => emailComposerFormats, []);

  useEffect(() => {
    if (!showComposer) return undefined;
    const timer = window.setTimeout(() => {
      try {
        prepareEmailComposerQuill(quillRef.current?.getEditor?.());
      } catch (_) {
        /* ignore */
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [showComposer, composerEditorKey]);

  const handleQuillChange = (val, _delta, source) => {
    if (source === 'user') {
      composerHtmlRef.current = val;
    }
  };

  const getComposerDraftHtml = () =>
    composerHtmlRef.current || composerData.html || '<p><br></p>';

  const handleAiReplyQuick = async () => {
    if (!email || aiReplyLoading) return;
    setAiReplyLoading(true);
    try {
      if (!showComposer || (composerMode !== 'reply' && composerMode !== 'replyAll')) {
        openComposer('reply');
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
        });
      }
      const incoming = buildIncomingEmailPayload(email);
      const text = await requestEmailAssist({ mode: 'draft_reply', incoming });
      applyHtmlToComposer({
        html: plainTextToQuillHtml(text),
        composerHtmlRef,
        setComposerData,
        setComposerEditorKey,
      });
      toast.success('Antwortentwurf eingefügt — bitte prüfen und senden');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'KI-Antwort fehlgeschlagen');
    } finally {
      setAiReplyLoading(false);
    }
  };

  const handleEmojiClick = (emoji) => {
    const symbol = emoji.native;
    if (composerData.html && composerData.html.length > 0) {
      handleComposerChange('html', `${composerData.html}${symbol}`);
    } else {
      handleComposerChange('html', symbol);
    }
  };

  if (loading) {
    return <PageSkeleton variant="detail" />;
  }

  if (!email) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/inbox')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zur Inbox
          </button>
        </div>
        
        <div className="card">
          <div className="card-body text-center py-12">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              E-Mail nicht gefunden
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Die angeforderte E-Mail konnte nicht geladen werden.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-[env(safe-area-inset-bottom)]">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          onClick={() => navigate('/inbox')}
          className="inline-flex items-center self-start text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors touch-manipulation py-1"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Zurück zur Inbox
        </button>
        
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => openComposer('reply')}
            className="btn-secondary flex items-center !py-2.5 sm:!py-2 touch-manipulation flex-1 sm:flex-none justify-center"
          >
            <Reply className="w-4 h-4 mr-2" />
            Antworten
          </button>
          <button
            type="button"
            disabled={aiReplyLoading}
            onClick={handleAiReplyQuick}
            className="btn-secondary flex items-center !py-2.5 sm:!py-2 touch-manipulation flex-1 sm:flex-none justify-center border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300"
            title="KI schlägt eine Antwort vor — bearbeiten und absenden"
          >
            {aiReplyLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            KI-Antwort
          </button>
          <button
            onClick={() => openComposer('replyAll')}
            className="btn-secondary flex items-center !py-2.5 sm:!py-2 touch-manipulation flex-1 sm:flex-none justify-center"
          >
            <ReplyAll className="w-4 h-4 mr-2" />
            <span className="sm:hidden">Allen</span>
            <span className="hidden sm:inline">Allen antworten</span>
          </button>
          <button
            onClick={() => openComposer('forward')}
            className="btn-secondary flex items-center !py-2.5 sm:!py-2 touch-manipulation flex-1 sm:flex-none justify-center"
          >
            <Forward className="w-4 h-4 mr-2" />
            Weiterleiten
          </button>
          {email?.subscription?.canUnsubscribe ? (
            <button
              type="button"
              disabled={unsubscribing}
              onClick={unsubscribeFromEmail}
              className="btn-secondary flex items-center !py-2.5 sm:!py-2 touch-manipulation text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800"
              title="Newsletter / Abo abmelden"
            >
              <UserMinus className="w-4 h-4 mr-2" />
              {unsubscribing ? '…' : 'Abmelden'}
            </button>
          ) : null}
          <button
            onClick={() => openComposer('new')}
            className="btn-primary flex items-center !py-2.5 sm:!py-2 touch-manipulation w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue E-Mail
          </button>
        </div>
      </div>

      {/* E-Mail Details */}
      <div className="card overflow-hidden">
        <div className="card-header !px-3 sm:!px-6">
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 break-words">
                {email.subject || '(Kein Betreff)'}
              </h1>
              
              <div className="space-y-2 text-sm min-w-0">
                <div className="flex items-start min-w-0">
                  <User className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">Von:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 break-all min-w-0">{email.from}</span>
                </div>
                
                <div className="flex items-start min-w-0">
                  <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">An:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 break-all min-w-0">{email.to}</span>
                </div>
                
                {email.cc && (
                  <div className="flex items-start min-w-0">
                    <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">CC:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400 break-all min-w-0">{email.cc}</span>
                  </div>
                )}
                
                <div className="flex items-start min-w-0">
                  <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">Datum:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400 break-words min-w-0">{formatDate(email.date)}</span>
                </div>
                
                {email.attachments && email.attachments.length > 0 && (
                  <div className="flex items-center">
                    <Paperclip className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">Anhänge:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">
                      {email.attachments.length} Datei(en)
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-shrink-0">
              {email.flags && email.flags.includes('\\Seen') ? (
                <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  Gelesen
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  Ungelesen
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="card-body !px-3 sm:!px-6 email-reading-scroll overflow-x-hidden min-w-0">
          {/* Debug Info */}
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
            <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">Debug Info:</h4>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 break-words">
              E-Mail geladen: {email ? 'Ja' : 'Nein'} | 
              Text: {email?.text?.length || 0} Zeichen | 
              HTML: {email?.html?.length || 0} Zeichen |
              Subject: {email?.subject || 'Kein Betreff'}
            </p>
          </div>
          
          {/* E-Mail Content */}
          <div className="email-reading-inner min-w-0 max-w-full">
            {email.html ? (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  {preferences.blockExternalContent && !allowExternalImages && email.html.includes('<img') ? (
                    <div className="flex-1 min-w-0 p-3 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <span>Externe Bilder wurden aus Sicherheitsgründen blockiert (Tracker-Schutz).</span>
                      <button onClick={() => setAllowExternalImages(true)} className="px-3 py-2 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 touch-manipulation self-start">Bilder laden</button>
                    </div>
                  ) : (
                    <span />
                  )}
                  {isDarkMode && (
                    <button
                      type="button"
                      onClick={toggleEmailInvert}
                      className={`inline-flex items-center rounded-lg border text-[11px] font-medium px-2.5 py-2 touch-manipulation ${
                        invertEmailInDark
                          ? 'border-neutral-900 bg-neutral-100 text-neutral-900 dark:border-neutral-300 dark:bg-neutral-800 dark:text-white'
                          : 'border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                      }`}
                      title="Helle E-Mail-Inhalte für Dark Mode invertieren"
                    >
                      <Contrast className="w-3 h-3 mr-1" />
                      {invertEmailInDark ? 'Originalfarben' : 'Invertieren'}
                    </button>
                  )}
                </div>
                <EmailHtmlBody
                  html={email.html}
                  className={emailHtmlClassName}
                  allowExternalImages={allowExternalImages}
                />
              </>
            ) : (
              <pre className="whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100 font-sans">
                {email.text || 'Keine Nachricht verfügbar'}
              </pre>
            )}
          </div>
          
          {/* Anhänge */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                Anhänge ({email.attachments.length})
              </h4>
              <div className="space-y-2">
                {email.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Paperclip className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-3" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {attachment.filename || 'Unbenannte Datei'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {attachment.contentType} • {(attachment.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Debug Info */}
          {email.messageId && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <details className="text-xs text-gray-500 dark:text-gray-400">
                <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                  Technische Details
                </summary>
                <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded font-mono">
                  <p><strong>Message-ID:</strong> {email.messageId}</p>
                  <p><strong>UID:</strong> {email.uid}</p>
                  {email.flags && <p><strong>Flags:</strong> {email.flags.join(', ')}</p>}
                </div>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* E-Mail Composer Modal */}
      {showComposer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-4xl h-[96dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 sm:p-6 py-3 sm:py-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                {composerMode === 'reply' && 'Antworten'}
                {composerMode === 'replyAll' && 'Allen antworten'}
                {composerMode === 'forward' && 'Weiterleiten'}
                {composerMode === 'new' && 'Neue E-Mail'}
              </h3>
              <button
                onClick={() => setShowComposer(false)}
                className="p-2 -mr-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation"
                aria-label="Schließen"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0" onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); sendEmail(); } }}>
              <div className="space-y-4">
                {/* To Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    An: *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                      <AtSign className="h-4 w-4 text-gray-400" />
                    </div>
                    <RecipientSuggestInput
                      value={composerData.to}
                      onChange={(v) => handleComposerChange('to', v)}
                      className="w-full"
                      inputClassName="form-input w-full pl-9 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="empfaenger@example.com"
                      required
                    />
                  </div>
                </div>
                
                {/* CC/BCC toggles */}
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setShowCc((v) => !v)} className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    CC
                  </button>
                  <button type="button" onClick={() => setShowBcc((v) => !v)} className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    BCC
                  </button>
                </div>
                {showCc && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC:</label>
                    <RecipientSuggestInput
                      value={composerData.cc}
                      onChange={(v) => handleComposerChange('cc', v)}
                      className="w-full"
                      inputClassName="form-input w-full"
                      placeholder="cc@example.com"
                    />
                  </div>
                )}
                {showBcc && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BCC:</label>
                    <RecipientSuggestInput
                      value={composerData.bcc}
                      onChange={(v) => handleComposerChange('bcc', v)}
                      className="w-full"
                      inputClassName="form-input w-full"
                      placeholder="bcc@example.com"
                    />
                  </div>
                )}
                
                {/* Subject Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Betreff: *
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Type className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      ref={subjectRef}
                      type="text"
                      value={composerData.subject}
                      onFocus={() => setInsertTarget('subject')}
                      onChange={(e) => handleComposerChange('subject', e.target.value)}
                      className="form-input w-full pl-9 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Betreff der E-Mail"
                      required
                    />
                  </div>
                </div>
                
                {/* Message Field - Rich Text */}
                <div>
                  <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Nachricht: *
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <EmailComposerAiButtons
                        composerMode={composerMode}
                        sourceEmail={email}
                        getDraftHtml={getComposerDraftHtml}
                        composerHtmlRef={composerHtmlRef}
                        setComposerData={setComposerData}
                        setComposerEditorKey={setComposerEditorKey}
                      />
                      <button type="button" onClick={() => setShowEmoji((v) => !v)} className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400">
                        😊 Emoji
                      </button>
                    </div>
                  </div>
                  {showEmoji && (
                    <div className="mb-2">
                      <Picker data={data} onEmojiSelect={handleEmojiClick} />
                    </div>
                  )}
                  <div className="quill-container" onFocusCapture={() => setInsertTarget('content')}>
                    <ReactQuill
                      key={composerEditorKey}
                      ref={(instance) => {
                        quillRef.current = instance;
                        if (instance) {
                          window.requestAnimationFrame(() => {
                            try {
                              prepareEmailComposerQuill(instance.getEditor?.());
                            } catch (_) {
                              /* ignore */
                            }
                          });
                        }
                      }}
                      theme="snow"
                      defaultValue={composerData.html || '<p><br></p>'}
                      onChange={handleQuillChange}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Ihre Nachricht..."
                      style={{ backgroundColor: 'transparent' }}
                    />
                  </div>
                  <VariableInsertPanel
                    variables={templateVars}
                    insertTarget={insertTarget}
                    onInsert={insertComposerPlaceholder}
                    className="mt-3"
                  />
                  {composerQuoteHtml ? (
                    <div className="composer-quote-preview mt-3 max-h-56 overflow-y-auto rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/80 px-3 py-2.5">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">
                        Originalnachricht
                      </p>
                      <div
                        className="composer-quote-body text-[13px] leading-relaxed text-neutral-600 dark:text-neutral-300"
                        dangerouslySetInnerHTML={{ __html: composerQuoteHtml }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex-shrink-0">
              <button
                onClick={() => {
                  setShowComposer(false);
                  setComposerQuoteHtml('');
                }}
                className="btn-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={sendEmail}
                disabled={sending}
                className="btn-primary flex items-center"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Wird gesendet...' : 'Senden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDetail;
